// src/views/Inventory/PengeluaranBarang/FormPengeluaranBarang.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Input,
  DatePicker,
  Select,
  Button,
  Typography,
  Space,
  message,
  Modal,
  Spin,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../../../configs"; // sesuaikan path
import Title from "antd/es/typography/Title";
import {
  autoCorrectLotNumber,
  LOT_NUMBER_FORMAT,
} from "../../../../utils/lotNumberValidator";

const { Text } = Typography;
const { Option } = Select;

// === KONSTAN ===
const NPWP = "063239834304400";

const DOC_OPTIONS = [
  { label: "BC 2.7", value: 27 },
  { label: "BC 2.8", value: 28 },
  { label: "BC 3.3", value: 33 },
  { label: "P3BET", value: 331 },
];

// generate SJ-YYMMDD001
const generateDeliveryNumber = (seq = 1) => {
  const today = dayjs().format("YYMMDD");
  return `SJ-${today}${String(seq).padStart(3, "0")}`;
};

export default function FormPengeluaranBarang() {
  const [form] = Form.useForm();

  const [nopenOptions, setNopenOptions] = useState([]);
  const [rawNopen, setRawNopen] = useState([]);
  const [loadingNopen, setLoadingNopen] = useState(false);
  const [loadingBarang, setLoadingBarang] = useState(false);
  const [saving, setSaving] = useState(false);

  // Confirmation Modal State
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  // State untuk menyimpan warning Nomor Lot
  const [lotWarnings, setLotWarnings] = useState({});

  /**
   * Handler untuk auto-correct dan validasi Nomor Lot saat onBlur
   */
  const handleLotBlur = (fieldIndex, value) => {
    if (!value) {
      setLotWarnings((prev) => {
        const newWarnings = { ...prev };
        delete newWarnings[fieldIndex];
        return newWarnings;
      });
      return;
    }

    const result = autoCorrectLotNumber(value);
    const warningKey = fieldIndex;

    if (result.isValid) {
      const items = form.getFieldValue("items") || [];
      if (items[fieldIndex]) {
        items[fieldIndex].lot = result.corrected;
        form.setFieldsValue({ items });
        message.success(`Format LOT diperbaiki: ${result.corrected}`);
      }
      setLotWarnings((prev) => {
        const newWarnings = { ...prev };
        delete newWarnings[warningKey];
        return newWarnings;
      });
    } else {
      setLotWarnings((prev) => ({
        ...prev,
        [warningKey]: result.warning,
      }));
      message.warning(`Format LOT tidak valid: ${result.warning}`);
    }
  };

  const [selectedMeta, setSelectedMeta] = useState(null);
  const [deliveryNumber, setDeliveryNumber] = useState(
    generateDeliveryNumber(),
  );

  const itemsWatch = Form.useWatch("items", form);
  const totalBarangKeluar = useMemo(() => {
    if (!itemsWatch || !Array.isArray(itemsWatch)) return 0;
    return itemsWatch.reduce(
      (sum, row) => sum + (Number(row?.jumlahKeluar) || 0),
      0,
    );
  }, [itemsWatch]);

  useEffect(() => {
    // form.setFieldsValue({
    //   delivery_number: deliveryNumber,
    //   delivery_date: dayjs(),
    // });
  }, [deliveryNumber, form]);

  // ==== AMBIL LIST NOPEN BERDASARKAN JENIS DOK ==== //
  const handleJenisDokumenChange = async (kodeDokumen) => {
    form.setFieldsValue({
      document_number: undefined,
      document_date: null,
      owner_name: undefined,
      container_number: undefined,
      items: [],
    });
    setSelectedMeta(null);
    setNopenOptions([]);
    setRawNopen([]);

    if (!kodeDokumen) return;
    let kodeRespon = "";
    switch (String(kodeDokumen)) {
      case "16":
        kodeRespon = "1603";
        break;
      case "27":
        kodeRespon = "2703";
        break;
      case "28":
        kodeRespon = "2803";
        break;
      case "33":
        kodeRespon = "3303";
        break;
      case "40":
        kodeRespon = "4003";
        break;
      case "331":
        kodeRespon = "33103";
        break;

      default:
        kodeRespon = "1603";
        break;
    }

    setLoadingNopen(true);
    try {
      const res = await axiosInstance.get("/portal/pasifik/get-data-nopen", {
        params: {
          kodeDokumen,
          kodeRespon,
        },
      });

      // Support both wrapped and unwrapped responses (API returns array directly)
      const rawList = Array.isArray(res.data) ? res.data : res.data?.data || [];

      // Map snake_case to camelCase
      const list = rawList.map((item) => ({
        ...item,
        nomorDaftar: item.nomorDaftar || item.nomor_daftar,
        tanggalDaftar: item.tanggalDaftar || item.tanggal_daftar,
        nomorAju: item.nomorAju || item.nomor_aju,
        kodeValuta: item.kodeValuta || item.kode_valuta,
        cif: item.cif,
        entitas: item.nomor_identitas, // Check if entitas is joined or needs fetching
        kontainer: item.kontainer,
        kemasan: item.kemasan,
      }));

      setRawNopen(list);
      setNopenOptions(
        list.map((item) => {
          const nopen = item.nomorDaftar || item.nomor_daftar;
          const owner = item.nama_entitas || item.namaEntitas || "-";
          const date = item.tanggalDaftar || item.tanggal_daftar;
          return {
            label: `${nopen} - ${owner} - ${dayjs(date).format("DD-MM-YYYY")}`,
            value: nopen,
          };
        }),
      );
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil daftar No Dokumen Pabean");
    } finally {
      setLoadingNopen(false);
    }
  };

  // ==== AMBIL DETAIL NOPEN + BARANG ==== //
  const handleNopenChange = async (nomorDaftar) => {
    if (!nomorDaftar) {
      setSelectedMeta(null);
      form.setFieldsValue({
        document_date: null,
        owner_name: undefined,
        container_number: undefined,
        items: [],
      });
      return;
    }

    const header = rawNopen.find((d) => d.nomorDaftar === nomorDaftar);
    if (!header) {
      message.error("Detail dokumen tidak ditemukan");
      return;
    }

    const entitas = header.entitas || [];
    const vendorEntitas = entitas[1] || null;
    const ownerEntitas = header.nomor_identitas || null;

    const nomorIdentitasVendor = vendorEntitas?.nomorIdentitas || null;

    // const ownerName = ownerEntitas || "";
    const kontainer = (header.kontainer || []).map((k) => k.nomorKontainer);
    const containerNumber = kontainer.join(",");

    const tanggalDaftar = header.tanggalDaftar || null;
    const kodeValuta = header.kodeValuta || null;
    const cif = header.cif || 0;

    const kemasanArr = header.kemasan || [];
    const kemasanInfo = kemasanArr[0] || {};
    const headerTotalKemasan = kemasanInfo.jumlahKemasan || 0;
    const merkKemasan = kemasanInfo.merkKemasan || null;

    setSelectedMeta({
      nomorDaftar,
      tanggalDaftar,
      kodeValuta,
      cif,
      nomorAju: header.nomorAju,
      kodeNegaraAsal: header.kodeNegaraAsal || null,
      containerNumber,
      id_contact: ownerEntitas,
      id_vendor: nomorIdentitasVendor,
      headerTotalKemasan,
      merkKemasan,
    });

    // Generate SJ number based on Nopen
    const generatedSJ = `SJ-${dayjs().format("YYYYMMDD")}-${nomorDaftar}`;

    form.setFieldsValue({
      delivery_number: generatedSJ,
      delivery_date: dayjs(),
      document_number: nomorDaftar,
      document_date: tanggalDaftar ? dayjs(tanggalDaftar) : null,
      owner_name: ownerEntitas
        ? String(ownerEntitas)
            .replace(/[^0-9]/g, "")
            .padStart(16, "0")
            .slice(0, 16)
        : "",
      container_number: containerNumber || undefined,
    });

    // === FETCH DOKUMEN ASAL & APPLY KODE PRIORITY (27 > 16) ===
    let allowedNopens = null; // null = tidak filter (ambil semua)
    try {
      const dokRes = await axiosInstance.get(
        "/portal/pasifik/get-data-dokumen",
        {
          params: { nomorPendaftaran: nomorDaftar },
        },
      );
      const dokList = Array.isArray(dokRes.data)
        ? dokRes.data
        : dokRes.data?.data || [];

      if (dokList.length > 0) {
        // Cek apakah ada dokumen asal kode 27 (BC 2.7)
        const dok27 = dokList.filter(
          (d) =>
            String(d.kode_dokumen || d.kodeDokumen) === "27" ||
            String(d.kode_dokumen || d.kodeDokumen) === "271",
        );
        const dok16 = dokList.filter(
          (d) => String(d.kode_dokumen || d.kodeDokumen) === "16",
        );

        if (dok27.length > 0) {
          // Prioritas: ambil hanya nopen dari kode 27
          allowedNopens = dok27
            .map((d) => String(d.nomor_dokumen || d.nomorDokumen))
            .filter(Boolean);
          console.log("Filter nopen asal → kode 27:", allowedNopens);
        } else if (dok16.length > 0) {
          // Fallback: ambil nopen dari kode 16
          allowedNopens = dok16
            .map((d) => String(d.nomor_dokumen || d.nomorDokumen))
            .filter(Boolean);
          console.log("Filter nopen asal → kode 16:", allowedNopens);
        }
      }
    } catch (err) {
      console.warn("Gagal mengambil dokumen asal, skip filter nopen:", err);
    }

    const kodeDokumen = form.getFieldValue("id_document");
    await fetchBarang(
      nomorDaftar,
      { kodeValuta, cif },
      allowedNopens,
      kodeDokumen,
      tanggalDaftar,
    );
  };

  // ==== AMBIL BARANG + EXPAND PER LOT DARI /api/item-stock (OPTIMIZED) ==== //
  const fetchBarang = async (
    nomorDaftar,
    metaFromHeader = {},
    allowedNopens = null,
    kodeDokumen = null,
    tanggalDaftar = null,
  ) => {
    setLoadingBarang(true);
    try {
      const params = { nomorPendaftaran: nomorDaftar };
      if (kodeDokumen) params.kodeDokumen = kodeDokumen;
      if (tanggalDaftar) {
        const dt = new Date(tanggalDaftar);
        params.tanggalDaftar = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
      }
      const res = await axiosInstance.get("/portal/pasifik/get-data-barang", {
        params,
      });

      // Support both wrapped and unwrapped responses
      const rawList = Array.isArray(res.data) ? res.data : res.data?.data || [];

      if (!rawList.length) {
        form.setFieldsValue({ items: [] });
        message.warning("Tidak ada data barang untuk dokumen ini");
        return;
      }

      // Transform snake_case to camelCase
      const list = rawList.map((r) => ({
        ...r,
        seriBarang: r.seri_barang || r.seriBarang,
        kodeBarang: r.kode_barang || r.kodeBarang,
        jumlahKemasan: r.jumlah_kemasan || r.jumlahKemasan,
        kodeJenisKemasan: r.kode_kemasan || r.kodeJenisKemasan || r.kodeKemasan,
        jumlahSatuan: r.jumlah_satuan || r.jumlahSatuan,
        kodeSatuanBarang: r.kode_satuan || r.kodeSatuanBarang || r.kodeSatuan,
        cifRupiah: r.cif_rupiah || r.cifRupiah,
        merk: r.merek || r.merk,
        uraian: r.uraian || r.uraianBarang,
        nomorDaftarAsal: r.nomor_daftar_asal || r.nomorDaftarAsal || null,
        tanggalDaftarAsal: r.tanggal_daftar_asal || r.tanggalDaftarAsal || null,
        seriBarangAsal: r.seri_barang_asal || r.seriBarangAsal || null,
      }));

      // Get unique item codes for batch stock fetch
      const uniqueItemCodes = [
        ...new Set(list.map((b) => b.kodeBarang).filter(Boolean)),
      ];

      // Fetch all stocks in PARALLEL (optimized)
      const stockPromises = uniqueItemCodes.map((code) =>
        axiosInstance
          .get("/item-stock", { params: { item_code: code, limit: 9999 } })
          .then((res) => ({ code, stocks: res.data?.data || [] }))
          .catch(() => ({ code, stocks: [] })),
      );
      const stockResults = await Promise.all(stockPromises);

      // Create a map for quick lookup
      const stockMap = {};
      stockResults.forEach(({ code, stocks }) => {
        stockMap[code] = stocks;
      });

      // Build rows from barang + stock data
      // Each barang item uses its own nomor_daftar_asal to filter stocks
      const allRows = [];
      for (const b of list) {
        // PRIORITY LOGIC:
        // 1. Use b.nomorDaftarAsal from API if present
        // 2. Else if allowedNopens is [nopen27], use nopen27
        // 3. Fallback to current nomorDaftar
        let itemNopenAsal = b.nomorDaftarAsal || null;
        if (
          !itemNopenAsal &&
          Array.isArray(allowedNopens) &&
          allowedNopens.length > 0
        ) {
          itemNopenAsal = allowedNopens[0];
        }
        if (!itemNopenAsal) itemNopenAsal = nomorDaftar;

        const baseMeta = {
          namaBarang: b.item_name
            ? `${b.item_name}—${b.kodeBarang}`
            : b.uraian
              ? `${b.uraian}—${b.kodeBarang}`
              : b.kodeBarang,
          nopenAsal: itemNopenAsal,
          tanggalNopenAsal: b.tanggalDaftarAsal
            ? dayjs(b.tanggalDaftarAsal).format("YYYY-MM-DD")
            : b.created_at
              ? dayjs(b.created_at).format("YYYY-MM-DD")
              : "-",
          seriAsal: b.seriBarangAsal,
          seri: b.seriBarang,
          stokSaatIni: 0,
          lot: "",
          jumlahKeluar: b.jumlahKemasan,
          kodeBarang: b.kodeBarang,
          merk: b.merk,
          kodeSatuanBarang: b.kodeSatuanBarang,
          jumlahSatuan: b.jumlahSatuan,
          kodeJenisKemasan: b.kodeJenisKemasan || b.kode_jenis_kemasan || "CT",
          cif: b.cif ?? metaFromHeader.cif ?? 0,
          idItemStock: null,
        };

        // Filter stocks per-item by its own nomor_daftar_asal
        let stockList = stockMap[b.kodeBarang] || [];
        if (itemNopenAsal && itemNopenAsal !== nomorDaftar) {
          // Item has a specific origin nopen — only show matching stocks
          // If no matching stocks, show stok=0 with correct nopen asal
          stockList = stockList.filter(
            (s) => String(s.nopen) === String(itemNopenAsal),
          );
          console.log(
            `Item ${b.kodeBarang} seri ${b.seriBarang}: nopenAsal=${itemNopenAsal}, stocks matched=${stockList.length}`,
          );
        }

        if (stockList.length === 0) {
          allRows.push({
            ...baseMeta,
            stokSaatIni: 0,
            lot: "-", // Use dash instead of empty if no stock
            jumlahKeluar: baseMeta.jumlahKeluar || 0,
            idItemStock: null,
          });
        } else {
          stockList.forEach((s) => {
            allRows.push({
              ...baseMeta,
              idIncomingDetail: s.id_incoming_detail || null,
              stokSaatIni: s.jumlah_kemasan,
              lot: s.lokasi_lot || s.lot || "-",
              jumlahKeluar: baseMeta.jumlahKeluar,
              idItemStock: s.id_item_stock,
              nopenAsal: s.nopen || baseMeta.nopenAsal,
              tanggalNopenAsal: s.tanggal_nopen
                ? dayjs(s.tanggal_nopen).format("YYYY-MM-DD")
                : baseMeta.tanggalNopenAsal
                  ? baseMeta.tanggalNopenAsal
                  : "-",
              kodeJenisKemasan:
                s.jenis_kemasan ||
                s.kemasan ||
                baseMeta.kodeJenisKemasan ||
                "CT",
            });
          });
        }
      }

      form.setFieldsValue({ items: allRows });
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil data barang");
    } finally {
      setLoadingBarang(false);
    }
  };

  // ==== SUBMIT KE BE OUTGOING (payload baru + update stok item-stock) ==== //
  const handleSubmit = async (values) => {
    if (!selectedMeta) {
      message.error("Silakan pilih Jenis & No Dokumen Pabean terlebih dahulu");
      return;
    }

    const items = values.items || [];
    if (!items.length) {
      message.error("Tidak ada barang yang akan dikeluarkan");
      return;
    }

    // VALIDASI: jumlahKeluar per baris tidak boleh > stokSaatIni
    const invalid = items.find(
      (row) =>
        Number(row.jumlahKeluar || 0) > Number(row.stokSaatIni || 0) &&
        Number(row.stokSaatIni || 0) > 0,
    );
    if (invalid) {
      message.error(
        "Jumlah keluar tidak boleh lebih besar dari stok saat ini per LOT",
      );
      return;
    }

    // Filter items yang jumlahKeluar > 0
    const validItems = items.filter(
      (row) => (Number(row.jumlahKeluar) || 0) > 0,
    );

    if (validItems.length === 0) {
      message.error("Tidak ada barang yang akan dikeluarkan (jumlah > 0)");
      return;
    }

    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    const values = pendingValues;
    const items = values.items || [];

    // Re-filter valid items (should be same as before)
    const validItems = items.filter(
      (row) => (Number(row.jumlahKeluar) || 0) > 0,
    );

    const { nomorDaftar, tanggalDaftar, kodeValuta, cif, id_contact } =
      selectedMeta;

    const headerPayload = {
      delivery_number: values.delivery_number,
      delivery_date: values.delivery_date
        ? dayjs(values.delivery_date).format("YYYY-MM-DD")
        : null,
      id_customer: id_contact ? String(id_contact).slice(0, 16) : null,
      document_number: nomorDaftar,
      document_date: tanggalDaftar
        ? dayjs(tanggalDaftar).format("YYYY-MM-DD")
        : null,
      id_document: values.id_document,
      id_cif_currency: kodeValuta,
      total_packaging: totalBarangKeluar,
      cif: cif,
      created_by: 0,
    };

    const detailsPayload = validItems.map((row) => ({
      id_incoming_detail: row.idIncomingDetail,
      id_customer: id_contact ? String(id_contact).slice(0, 16) : "",
      nopen: nomorDaftar,
      tanggal_nopen: tanggalDaftar
        ? dayjs(tanggalDaftar).format("YYYY-MM-DD")
        : null,
      nopen_asal: row.nopenAsal || nomorDaftar,
      tanggal_nopen_asal: row.tanggalNopenAsal || null,
      seri_asal: row.seriAsal,
      seri: row.seri,
      item_code: row.kodeBarang,
      kemasan: row.kodeJenisKemasan || row.kemasan || "CT",
      total_kemasan: Number(row.jumlahKeluar) || 0,
      lot: row.lot || "",
      // created_at: nowIso,
      created_by: 0,
      // updated_at: nowIso,
      updated_by: 0,
    }));

    try {
      setSaving(true);

      // 1) Simpan outgoing
      await axiosInstance.post("/outgoing", {
        header: headerPayload,
        details: detailsPayload,
      });

      message.success("Data pengeluaran & stok gudang berhasil disimpan");

      setDeliveryNumber(generateDeliveryNumber(2));
      form.resetFields();
      form.setFieldsValue({
        delivery_number: generateDeliveryNumber(),
        delivery_date: dayjs(),
      });
      setSelectedMeta(null);
      setNopenOptions([]);
      setRawNopen([]);
      setConfirmVisible(false);
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan pengeluaran barang / update stok");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Form Pengeluaran Barang
          </Title>
          <Text type="secondary">
            Kelola daftar Pengeluaran Barang untuk kebutuhan inventory &
            kepabeanan.
          </Text>
        </Col>
      </Row>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          items: [
            {
              jumlahKeluar: 0,
            },
          ],
        }}
      >
        {/* BARIS 1: No SJ & Tanggal */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No Surat Jalan</Text>}
              name="delivery_number"
              rules={[
                { required: true, message: "No Surat Jalan wajib diisi" },
              ]}
            >
              <Input placeholder="No Surat Jalan" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal Surat Jalan</Text>}
              name="delivery_date"
              rules={[
                { required: true, message: "Tanggal Surat Jalan wajib diisi" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} format="DD MMMM YYYY" />
            </Form.Item>
          </Col>
        </Row>

        {/* BARIS 2: Jenis Dok & No Dok */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Jenis Dokumen Pabean</Text>}
              name="id_document"
              rules={[
                { required: true, message: "Jenis dokumen wajib dipilih" },
              ]}
            >
              <Select
                placeholder="Jenis Dokumen Pabean"
                options={DOC_OPTIONS}
                onChange={handleJenisDokumenChange}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No Dokumen Pabean</Text>}
              name="document_number"
              rules={[{ required: true, message: "No dokumen wajib dipilih" }]}
            >
              <Select
                placeholder="Pilih No Dokumen Pabean"
                loading={loadingNopen}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const label = option?.label || option?.children || "";
                  return String(label)
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
                onChange={handleNopenChange}
                virtual={true}
                listHeight={300}
              >
                {nopenOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value} label={opt.label}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* BARIS 3: Tanggal Dok + Pemilik */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal Dokumen Pabean</Text>}
              name="document_date"
            >
              <DatePicker
                disabled
                style={{ width: "100%" }}
                format="DD MMMM YYYY"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label={<Text strong>Pemilik</Text>} name="owner_name">
              <Input disabled placeholder="Pemilik" />
            </Form.Item>
          </Col>
        </Row>

        {/* BARIS 4: No Container + No Plat */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No Kontainer</Text>}
              name="container_number"
              extra="Pisahkan dengan koma jika lebih dari satu"
            >
              <Input placeholder="No Kontainer" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No Plat Kendaraan</Text>}
              name="plat_number"
            >
              <Input placeholder="No Plat Kendaraan" />
            </Form.Item>
          </Col>
        </Row>

        {/* LAMPIRAN */}
        <Row style={{ marginTop: 8, marginBottom: 16 }}>
          <Col span={24}>
            <Text strong>Lampiran 1</Text>
          </Col>
          <Col span={24} style={{ marginTop: 8 }}>
            <Space direction="vertical" size={4}>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                style={{
                  background: "#FFA800",
                  borderColor: "#FFA800",
                  fontWeight: 600,
                }}
                onClick={() =>
                  document.getElementById("upload-lampiran-outgoing").click()
                }
              >
                Upload File
              </Button>
              <input
                id="upload-lampiran-outgoing"
                type="file"
                style={{ display: "none" }}
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    message.info(
                      `File "${file.name}" siap diupload (integrasikan dengan payload header.lampiran_1 jika diperlukan).`,
                    );
                  }
                }}
              />
              <Text type="secondary">
                Jenis File: JPG | JPEG | PNG | GIF | PDF | DOC | DOCX | XLS |
                XLSX. Maks 5 MB.
              </Text>
            </Space>
          </Col>
        </Row>

        {/* TABEL BARANG: NAMA, STOK, NOPEN, SERI, JUMLAH KELUAR, LOT */}
        <Spin spinning={loadingBarang} tip="Memuat data barang...">
          <Card
            bodyStyle={{ padding: 0 }}
            style={{
              marginTop: 16,
              borderRadius: 12,
              border: "1px solid #E4E6EF",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "2.2fr 1.2fr 1.4fr 1.4fr 0.7fr 1.1fr 1fr 70px",
                background: "#F3F6F9",
                padding: "8px 16px",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              <div>NAMA BARANG *</div>
              <div>STOK</div>
              <div>NOPEN ASAL</div>
              <div>TGL NOPEN</div>
              <div>SERI</div>
              <div>JML KELUAR</div>
              <div>LOT</div>
              <div style={{ textAlign: "center" }}>AKSI</div>
            </div>

            <Form.List name="items">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => {
                    const itemsVal = form.getFieldValue("items") || [];
                    const row = itemsVal[field.name] || {};

                    return (
                      <div key={field.key}>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "2.2fr 1.2fr 1.4fr 1.4fr 0.7fr 1.1fr 1fr 70px",
                            padding: "8px 16px",
                            borderTop: "1px solid #E4E6EF",
                            alignItems: "center",
                          }}
                        >
                          {/* Nama Barang */}
                          <Form.Item
                            {...field}
                            name={[field.name, "namaBarang"]}
                            fieldKey={[field.fieldKey, "namaBarang"]}
                            rules={[
                              {
                                required: true,
                                message: "Nama barang wajib diisi",
                              },
                            ]}
                            style={{ marginBottom: 0, paddingRight: 8 }}
                          >
                            <Input placeholder="Nama Barang" />
                          </Form.Item>

                          {/* Stok Saat Ini (dari item-stock) */}
                          <Form.Item
                            {...field}
                            name={[field.name, "stokSaatIni"]}
                            fieldKey={[field.fieldKey, "stokSaatIni"]}
                            style={{ marginBottom: 0, paddingRight: 8 }}
                          >
                            <Input placeholder="0" disabled />
                          </Form.Item>

                          {/* Nopen Asal */}
                          <Form.Item
                            {...field}
                            name={[field.name, "nopenAsal"]}
                            fieldKey={[field.fieldKey, "nopenAsal"]}
                            style={{ marginBottom: 0, paddingRight: 8 }}
                          >
                            <Input placeholder="Nopen Asal" />
                          </Form.Item>

                          {/* Tgl Nopen Asal */}
                          <Form.Item
                            {...field}
                            name={[field.name, "tanggalNopenAsal"]}
                            fieldKey={[field.fieldKey, "tanggalNopenAsal"]}
                            style={{ marginBottom: 0, paddingRight: 8 }}
                          >
                            <Input placeholder="Tgl Nopen" disabled />
                          </Form.Item>

                          {/* Seri */}
                          <Form.Item
                            {...field}
                            name={[field.name, "seri"]}
                            fieldKey={[field.fieldKey, "seri"]}
                            style={{ marginBottom: 0, paddingRight: 8 }}
                          >
                            <Input placeholder="Seri" />
                          </Form.Item>

                          {/* Jumlah Keluar */}
                          <Form.Item
                            {...field}
                            name={[field.name, "jumlahKeluar"]}
                            fieldKey={[field.fieldKey, "jumlahKeluar"]}
                            style={{ marginBottom: 0, paddingRight: 8 }}
                            rules={[
                              {
                                required: true,
                                message: "Jumlah keluar wajib diisi",
                              },
                            ]}
                          >
                            <Input placeholder="0" type="number" min={0} />
                          </Form.Item>

                          {/* LOT (prefill dari item-stock, bisa diedit kalau perlu) */}
                          <Form.Item
                            {...field}
                            name={[field.name, "lot"]}
                            fieldKey={[field.fieldKey, "lot"]}
                            style={{ marginBottom: 0, paddingRight: 8 }}
                            rules={[
                              {
                                required: true,
                                message: "LOT wajib diisi",
                              },
                            ]}
                            help={
                              lotWarnings[field.name] ? (
                                <span
                                  style={{ color: "#faad14", fontSize: 11 }}
                                >
                                  ⚠️ {lotWarnings[field.name]}
                                </span>
                              ) : null
                            }
                            validateStatus={
                              lotWarnings[field.name] ? "warning" : undefined
                            }
                            tooltip={LOT_NUMBER_FORMAT.tooltip}
                          >
                            <Input
                              placeholder={LOT_NUMBER_FORMAT.placeholder}
                              onBlur={(e) =>
                                handleLotBlur(field.name, e.target.value)
                              }
                              style={
                                lotWarnings[field.name]
                                  ? {
                                      borderColor: "#faad14",
                                      backgroundColor: "#fffbe6",
                                    }
                                  : undefined
                              }
                            />
                          </Form.Item>

                          <div style={{ textAlign: "center" }}>
                            <Button
                              type="text"
                              icon={
                                <DeleteOutlined style={{ color: "#F64E60" }} />
                              }
                              onClick={() => remove(field.name)}
                            />
                          </div>

                          {/* hidden meta utk payload & update stok */}
                          <Form.Item
                            name={[field.name, "kodeBarang"]}
                            fieldKey={[field.fieldKey, "kodeBarang"]}
                            hidden
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            name={[field.name, "merk"]}
                            fieldKey={[field.fieldKey, "merk"]}
                            hidden
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            name={[field.name, "kodeSatuanBarang"]}
                            fieldKey={[field.fieldKey, "kodeSatuanBarang"]}
                            hidden
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            name={[field.name, "jumlahSatuan"]}
                            fieldKey={[field.fieldKey, "jumlahSatuan"]}
                            hidden
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            name={[field.name, "kodeJenisKemasan"]}
                            fieldKey={[field.fieldKey, "kodeJenisKemasan"]}
                            hidden
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            name={[field.name, "cif"]}
                            fieldKey={[field.fieldKey, "cif"]}
                            hidden
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            name={[field.name, "idItemStock"]}
                            fieldKey={[field.fieldKey, "idItemStock"]}
                            hidden
                          >
                            <Input />
                          </Form.Item>
                        </div>
                      </div>
                    );
                  })}

                  <div style={{ padding: "8px 16px 12px" }}>
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          namaBarang: "",
                          stokSaatIni: 0,
                          nopenAsal: "",
                          seri: "",
                          jumlahKeluar: 0,
                          lot: "",
                          kodeBarang: "",
                          kodeJenisKemasan: "",
                          idItemStock: null,
                        })
                      }
                    >
                      Tambah Baris
                    </Button>
                  </div>
                </>
              )}
            </Form.List>
          </Card>
        </Spin>

        {/* TOTAL BARANG KELUAR */}
        <div
          style={{
            marginTop: 12,
            textAlign: "left",
            fontWeight: 600,
          }}
        >
          TOTAL BARANG KELUAR: {totalBarangKeluar}
        </div>

        {/* TOMBOL AKSI */}
        <Row justify="end" style={{ marginTop: 24 }}>
          <Space>
            <Button danger> Batal </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving || loadingBarang}
              style={{
                minWidth: 120,
                fontWeight: 600,
              }}
            >
              Simpan Pengeluaran
            </Button>
          </Space>
        </Row>
      </Form>

      <Modal
        title="Konfirmasi Pengeluaran Barang"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Simpan"
        cancelText="Batal"
        confirmLoading={saving}
        width={600}
      >
        <p>Apakah Anda yakin ingin menyimpan data pengeluaran barang ini?</p>
        <div style={{ background: "#f5f5f5", padding: 10, borderRadius: 4 }}>
          <p style={{ margin: 0 }}>
            <strong>No Surat Jalan:</strong> {pendingValues?.delivery_number}
          </p>
          <p style={{ margin: 0 }}>
            <strong>Tanggal:</strong>{" "}
            {pendingValues?.delivery_date
              ? dayjs(pendingValues.delivery_date).format("DD MMMM YYYY")
              : "-"}
          </p>
          <p style={{ margin: 0 }}>
            <strong>No Dokumen:</strong> {selectedMeta?.nomorDaftar}
          </p>
        </div>

        <div style={{ marginTop: 16 }}>
          <Text strong>Detail Barang Keluar:</Text>
          <div
            style={{
              maxHeight: 200,
              overflowY: "auto",
              marginTop: 8,
              border: "1px solid #f0f0f0",
            }}
          >
            <table
              style={{
                width: "100%",
                fontSize: 12,
                borderCollapse: "collapse",
              }}
            >
              <thead
                style={{ background: "#fafafa", position: "sticky", top: 0 }}
              >
                <tr>
                  <th
                    style={{
                      padding: "4px 8px",
                      textAlign: "left",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Nama Barang
                  </th>
                  <th
                    style={{
                      padding: "4px 8px",
                      textAlign: "right",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Jml Keluar
                  </th>
                  <th
                    style={{
                      padding: "4px 8px",
                      textAlign: "left",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Lot
                  </th>
                </tr>
              </thead>
              <tbody>
                {pendingValues?.items
                  ?.filter((i) => (Number(i.jumlahKeluar) || 0) > 0)
                  .map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "4px 8px" }}>{item.namaBarang}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>
                        {item.jumlahKeluar}
                      </td>
                      <td style={{ padding: "4px 8px" }}>{item.lot}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
